import { useMemo, useState } from "react";
import Modal from "react-modal";
import { useIntl } from "react-intl";
import OwTitle from "../OwTitle/OwTitle";
import OwLabel from "../OwLabel/OwLabel";
import OwButton from "../OwButton/OwButton";
import "./FilterablePickerModal.scss";

export interface IPickerItem {
    value: string;
    label: string;
    sublabel?: string;
}

type Props = {
    isOpen: boolean;
    /** i18n key for the modal title. */
    title: string;
    items: IPickerItem[];
    selectedValue?: string | null;
    /** Called with the chosen item's value. The caller is responsible for closing. */
    onSelect: (value: string) => void;
    onClose: () => void;
    /** i18n key for the search input placeholder. */
    searchPlaceholder?: string;
    /** When true, shows a "None" entry that selects an empty value (clears the field). */
    allowClear?: boolean;
};

/**
 * Reusable filterable single-select picker rendered as a modal. Built for large option
 * sets (e.g. IANA timezones) where a native dropdown is unusable. Generic over value/label
 * so it can be reused anywhere a searchable pick-one overlay is needed.
 */
export const FilterablePickerModal = (props: Props) => {
    const intl = useIntl();
    const [query, setQuery] = useState("");

    const normalized = query.trim().toLowerCase();
    const filtered = useMemo(() => {
        if (!normalized) return props.items;
        return props.items.filter(
            (i) => i.label.toLowerCase().includes(normalized) || (i.sublabel ? i.sublabel.toLowerCase().includes(normalized) : false)
        );
    }, [normalized, props.items]);

    const placeholder = intl.formatMessage({ id: props.searchPlaceholder || "search", defaultMessage: "Search..." });

    return (
        <Modal
            isOpen={props.isOpen}
            className="modalContentClass FilterablePickerModal"
            overlayClassName="modalOverlayClass"
            contentLabel={intl.formatMessage({ id: props.title, defaultMessage: props.title })}
            shouldCloseOnOverlayClick={true}
            onRequestClose={props.onClose}>
            <div className="p-3">
                <div className="d-flex justify-content-between align-items-center">
                    <OwTitle title={props.title} tag="h5" />
                    <OwButton fn={props.onClose} centerIconClass="fas fa-times" className="btn-secondary btn-sm" ariaLabel="close" />
                </div>

                <input
                    type="text"
                    className="form-control form-control-sm mt-2"
                    placeholder={placeholder}
                    aria-label={placeholder}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    autoFocus
                />

                <div className="picker-list mt-2" role="listbox">
                    {props.allowClear && (
                        <button
                            type="button"
                            role="option"
                            aria-selected={!props.selectedValue}
                            className={"picker-item" + (!props.selectedValue ? " selected" : "")}
                            onClick={() => props.onSelect("")}>
                            <OwLabel title="none" noBottomMargin={true} />
                        </button>
                    )}
                    {filtered.length === 0 ? (
                        <OwLabel title="no_results" className="text-muted d-block p-2" />
                    ) : (
                        filtered.map((item) => (
                            <button
                                key={item.value}
                                type="button"
                                role="option"
                                aria-selected={props.selectedValue === item.value}
                                className={"picker-item" + (props.selectedValue === item.value ? " selected" : "")}
                                onClick={() => props.onSelect(item.value)}>
                                <span>{item.label}</span>
                                {item.sublabel ? <span className="sublabel ms-2 text-muted">{item.sublabel}</span> : null}
                            </button>
                        ))
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default FilterablePickerModal;
